/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState, useEffect } from "react";
import {
  getNotifications,
  getNotificationsCount,
  markAllNotificationsRead,
  markNotificationRead,
} from "./service";
import {
  NOTIFICATION_ENTITY,
  NOTIFICATION_TYPE,
  NotificationListItem,
} from "./types";
import { useDispatch } from "react-redux";
import { setNotificationCount } from "./notificationSlice";
import { useNavigate } from "react-router-dom";

export const useNotifications = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<NotificationListItem[]>(
    []
  );
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    fetchNotificationsCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchNotificationsCount = async () => {
    try {
      setLoading(true);
      const data = await getNotificationsCount();
      dispatch(setNotificationCount(data.count));
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const getNotificationsList = async () => {
    try {
      setLoading(true);
      const data = await getNotifications();
      setNotifications(data);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const markAllAsRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      fetchNotificationsCount();
    } catch (error) {
      console.error("Failed to mark all as read:", error);
    }
  };

  const markAsRead = async (id: string) => {
    try {
      await markNotificationRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
      );
      fetchNotificationsCount();
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  const handleCtaClick = async (notification: NotificationListItem) => {
    if (notification.hasCta) {
      if (!notification.isRead) {
        await markNotificationRead(notification.id);
      }
      if (notification.entity === NOTIFICATION_ENTITY.COLLECTION) {
        navigate(`../collectionDetail/${notification.entityId}`, {
          replace: true,
        });
      } else if (
        notification.entity === NOTIFICATION_ENTITY.CAMPAIGN &&
        (notification.type === NOTIFICATION_TYPE.CAMPAIGN_COMPLETED ||
          notification.type === NOTIFICATION_TYPE.CAMPAIGN_STARTED)
      ) {
        navigate(`../campaignDetails/${notification.entityId}`);
      } else if (
        notification.entity === NOTIFICATION_ENTITY.SUBSCRIPTION &&
        notification.type === NOTIFICATION_TYPE.SUBSCRIPTION_PAYMENT_FAILED
      ) {
        navigate(`../billing`);
      } else if (
        notification.entity === NOTIFICATION_ENTITY.CAMPAIGN &&
        notification.type === NOTIFICATION_TYPE.CAMPAIGN_CREATED
      ) {
        navigate(`../campaign`);
      } else if (
        notification.entity === NOTIFICATION_ENTITY.USER_FEATURE_QUOTA &&
        notification.type === NOTIFICATION_TYPE.QUOTA_UNUSED
      ) {
        navigate(`../`);
      } else if (
        notification.entity === NOTIFICATION_ENTITY.USER_FEATURE_QUOTA &&
        notification.type === NOTIFICATION_TYPE.QUOTA_RENEWED
      ) {
        navigate(`../campaignLanding`);
      } else if (notification.entity === NOTIFICATION_ENTITY.DEAL) {
        navigate(`/deals/${notification?.entityId}`);
      } else {
        console.warn("Unknown notification type:", notification.type);
      }
    }
  };

  return {
    loading,
    notifications,
    handleCtaClick,
    getNotificationsList,
    markAllAsRead,
    markAsRead,
  };
};
